import type { DeepKeys } from '@tanstack/react-form'
import type { OrganizationId } from '@/lib/organization-scoping'
import type {
  SuperTicketTemplate,
  SuperTicketTemplateSettings,
} from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import type { SuperTicketEditorLayout } from './SuperTicketEditor'
import { useState } from 'react'
import { z } from 'zod/v4'
import { Ticket } from 'lucide-react'
import { toast } from 'sonner'
import SaveBar from '@/components/forms/SaveBar'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePrintWindow } from '@/hooks/usePrintWindow'
import { submitSchemaValidator, useAppForm } from '@/lib/form'
import { getErrorMessage } from '@/lib/utils'
import { useSaveSuperTicketTemplate } from '@/lib/services/super-ticket-settings/super-ticket-settings.query'
import {
  editableSuperTicketLayout,
  superTicketConflict,
  superTicketLayoutChanges,
  superTicketLayoutSchema,
} from '@/lib/services/super-ticket-settings/super-ticket-settings.schema'
import { SuperTicketLayoutEditor } from './SuperTicketEditor'

type TicketDrafts = Partial<Record<SuperTicketTemplate, SuperTicketEditorLayout>>
type SettingsSession = {
  catalog: SuperTicketTemplateSettings[]
  revision: number
  drafts?: TicketDrafts
  error?: string
  conflict?: SuperTicketTemplateSettings | null
}
type SettingsProps = {
  organizationId: OrganizationId
  catalog: SuperTicketTemplateSettings[]
  isReadOnly?: boolean
}
type SettingsFormProps = Pick<SettingsProps, 'organizationId' | 'isReadOnly'> & {
  session: SettingsSession
  template: string
  onTemplateChange: (template: string) => void
  initialSection?: string
  onSectionChange: (section: string) => void
  onRebase: (session: Omit<SettingsSession, 'revision'>) => void
}

export function SuperTicketSettings({ organizationId, catalog, isReadOnly }: SettingsProps) {
  // The page owns drafts for every template. Background refetches cannot replace edits.
  const [session, setSession] = useState<SettingsSession>({ catalog, revision: 0 })
  // Keep navigation choices when a save/discard rebuilds the form's baseline.
  const [sections, setSections] = useState<Record<string, string>>({})
  const templateStorageKey = `super-ticket-settings.template.${organizationId}`
  const [template, setTemplate] = useState<string | undefined>(() => {
    try {
      const stored = window.localStorage.getItem(templateStorageKey)
      return catalog.find((entry) => entry.template === stored)?.template ?? catalog[0]?.template
    } catch {
      return catalog[0]?.template
    }
  })

  function changeTemplate(nextTemplate: string) {
    setTemplate(nextTemplate)
    try {
      window.localStorage.setItem(templateStorageKey, nextTemplate)
    } catch {
      // Remembering a selection is optional when browser storage is unavailable.
    }
  }

  if (template === undefined) return null
  return (
    <SuperTicketSettingsForm
      key={session.revision}
      organizationId={organizationId}
      session={session}
      template={template}
      onTemplateChange={changeTemplate}
      initialSection={sections[template]}
      onSectionChange={(section) => setSections((current) => ({ ...current, [template]: section }))}
      isReadOnly={isReadOnly}
      onRebase={(next) => setSession({ ...next, revision: session.revision + 1 })}
    />
  )
}

function SuperTicketSettingsForm({
  organizationId,
  session,
  template,
  onTemplateChange,
  initialSection,
  onSectionChange,
  isReadOnly,
  onRebase,
}: SettingsFormProps) {
  const conflict = session.conflict
  const save = useSaveSuperTicketTemplate()
  const { openPrintWindow } = usePrintWindow({
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const [initial] = useState<TicketDrafts>(
    () =>
      session.drafts ??
      Object.fromEntries(
        session.catalog.map((metadata) => [
          metadata.template,
          editableSuperTicketLayout(metadata.settings),
        ])
      )
  )
  const form = useAppForm({
    defaultValues: initial,
    validators: {
      onSubmit: submitSchemaValidator(
        z.object(
          Object.fromEntries(
            session.catalog.map((metadata) => [
              metadata.template,
              superTicketLayoutSchema(metadata),
            ])
          )
        )
      ),
    },
    onSubmit: async ({ value }) => {
      if (isReadOnly) return
      const catalog = [...session.catalog]
      for (const [index, metadata] of catalog.entries()) {
        const layout = value[metadata.template]
        if (!layout) continue
        const changes = superTicketLayoutChanges(metadata, layout)
        if (!changes) continue
        try {
          catalog[index] = await save.mutateAsync({
            organizationId,
            template: metadata.template,
            version: metadata.version,
            layout: changes,
          })
        } catch (error) {
          // Keep unsaved drafts while adopting versions for any templates already saved.
          onRebase({
            catalog,
            drafts: value,
            error: `Could not save ${metadata.label}. ${getErrorMessage(error)}`,
            conflict: superTicketConflict(error),
          })
          return
        }
      }
      onRebase({ catalog })
      toast.success('Superticket defaults saved')
    },
  })

  return (
    <form.AppForm>
      <form.Form className="max-w-7xl space-y-5">
        <AdminPageHeader
          icon={Ticket}
          title="Supertickets"
          description="Make each paper template fit your shop. Adjust the preview, then save defaults for future prints."
        />
        <form.Subscribe
          selector={(state) => [state.values, state.isSubmitting] satisfies [TicketDrafts, boolean]}
        >
          {([layouts, isSubmitting]) => {
            const metadata = session.catalog.find((entry) => entry.template === template)
            const layout = metadata ? layouts[metadata.template] : undefined
            if (!metadata || !layout) return null
            const hasChanges = session.catalog.some((entry) => {
              const entryLayout = layouts[entry.template]
              return entryLayout ? superTicketLayoutChanges(entry, entryLayout) : false
            })
            return (
              <>
                <Card>
                  <CardContent className="p-4">
                    <div className="w-full max-w-md space-y-2">
                      <Label htmlFor="settings-ticket-template">Paper template</Label>
                      <Select
                        value={template}
                        disabled={isSubmitting}
                        onValueChange={onTemplateChange}
                      >
                        <SelectTrigger id="settings-ticket-template">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {session.catalog.map((entry) => (
                            <SelectItem key={entry.template} value={entry.template}>
                              {entry.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
                {session.error ? (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {session.error}
                      {conflict ? (
                        <Button
                          type="button"
                          variant="link"
                          disabled={isSubmitting}
                          onClick={() =>
                            onRebase({
                              catalog: session.catalog.map((entry) =>
                                entry.template === conflict.template ? conflict : entry
                              ),
                              drafts: {
                                ...layouts,
                                [conflict.template]: editableSuperTicketLayout(conflict.settings),
                              },
                            })
                          }
                        >
                          Reload saved settings for {conflict.label}
                        </Button>
                      ) : null}
                    </AlertDescription>
                  </Alert>
                ) : null}
                <SuperTicketLayoutEditor
                  key={template}
                  organizationId={organizationId}
                  metadata={metadata}
                  initialSection={initialSection}
                  onSectionChange={onSectionChange}
                  layout={layout}
                  resetLayout={editableSuperTicketLayout(metadata.defaults)}
                  onFieldChange={(field, value) =>
                    form.setFieldValue<DeepKeys<TicketDrafts>>(
                      `${metadata.template}.${field}`,
                      value
                    )
                  }
                  onReset={() =>
                    form.setFieldValue(
                      metadata.template,
                      editableSuperTicketLayout(metadata.defaults)
                    )
                  }
                  isReadOnly={isReadOnly}
                  isSubmitting={isSubmitting}
                  showSubmit={false}
                  onPrintTest={({ html }) => openPrintWindow({ html, autoPrint: true })}
                />
                {!isReadOnly && hasChanges ? (
                  <SaveBar
                    forceDirty
                    mutation={save}
                    resetButtonText="Discard changes"
                    onReset={() => onRebase({ catalog: session.catalog })}
                  />
                ) : null}
              </>
            )
          }}
        </form.Subscribe>
      </form.Form>
    </form.AppForm>
  )
}
